(function () {
  'use strict';

  const CURRENCY_DIVISORS = Object.freeze({
    BIF: 1,
    CLP: 1,
    DJF: 1,
    GNF: 1,
    ISK: 1,
    JPY: 1,
    KMF: 1,
    KRW: 1,
    PYG: 1,
    RWF: 1,
    UGX: 1,
    VND: 1,
    VUV: 1,
    XAF: 1,
    XOF: 1,
    XPF: 1,
  });

  function normalizeKey(value) {
    let normalized = (value || '').toString().toLowerCase().trim();
    if (normalized.normalize) {
      normalized = normalized
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/g, '');
    }

    return normalized
      .replace(/[_/+]+/g, ' ')
      .replace(/-/g, ' ')
      .replace(/[^\w\u0600-\u06FF ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function esc(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function escAttr(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function getMoneyDivisor(currency) {
    // Shopify storefront JSON surfaces (product.js, cart.js, theme JSON payloads)
    // use 100-based amounts for non-zero-decimal currencies on this storefront,
    // including KWD. Intl handles the visible 3-decimal currency rendering.
    return CURRENCY_DIVISORS[(currency || '').toUpperCase()] || 100;
  }

  function formatMoney(amount, currency) {
    if (amount == null || amount === '') return '';

    const locale = (document.documentElement.lang || '').toLowerCase().startsWith('ar') ? 'ar-KW' : 'en-KW';
    const safeCurrency = (currency || 'KWD').toUpperCase();
    const divisor = getMoneyDivisor(safeCurrency);
    const value = Number(amount) / divisor;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: safeCurrency,
      }).format(value);
    } catch (error) {
      return `${value.toFixed(divisor === 1000 ? 3 : divisor === 1 ? 0 : 2)} ${safeCurrency}`.trim();
    }
  }

  function toMinorUnits(amount, currency) {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed)) return 0;
    return Math.round(parsed * getMoneyDivisor(currency));
  }

  function replaceTokens(template, values) {
    if (!template) return '';
    let output = template.replace(/\{\{\s*(\w+)\s*\}\}/g, function replaceMatch(match, key) {
      return Object.prototype.hasOwnProperty.call(values || {}, key) ? values[key] : '';
    });
    Object.entries(values || {}).forEach(function replaceFallbackToken(entry) {
      const token = '__' + String(entry[0]).toUpperCase() + '__';
      output = output.split(token).join(entry[1]);
    });
    return output;
  }

  function resolveCartAddUrl() {
    return resolveStorefrontPath('/cart/add.js');
  }

  function resolveCartUpdateUrl() {
    return resolveStorefrontPath('/cart/update');
  }

  function resolveStorefrontPath(path) {
    const safePath = (path || '').toString().trim();
    if (!safePath) return '/';

    const normalizedPath = safePath.startsWith('/') ? safePath : `/${safePath}`;
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    if (!root || root === '/') return normalizedPath;

    const normalizedRoot = root.endsWith('/') ? root.slice(0, -1) : root;
    return `${normalizedRoot}${normalizedPath}`;
  }

  function resolveCheckoutUrl() {
    return resolveStorefrontPath('/checkout');
  }

  function buildCheckoutRedirectUrl(offerActivation) {
    const discountCode = offerActivation && offerActivation.code
      ? offerActivation.code.toString().trim()
      : '';

    if (!discountCode) return resolveCheckoutUrl();
    return `${resolveStorefrontPath(`/discount/${encodeURIComponent(discountCode)}`)}?redirect=${encodeURIComponent(resolveCheckoutUrl())}`;
  }

  function performCheckoutRedirect(url) {
    const hooks = window.__taraConsultationTestHooks || null;
    if (hooks && typeof hooks.onCheckoutRedirect === 'function') {
      hooks.onCheckoutRedirect(url);
      return url;
    }

    window.location.assign(url);
    return url;
  }

  function getCartSectionIds() {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function collect(item) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    return sectionIds;
  }

  function normalizeDiscountCode(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function getCurrentDiscountCodes() {
    return Array.from(document.querySelectorAll('.cart-discount__pill[data-discount-code]'))
      .map(function mapDiscountCode(pill) {
        return pill instanceof HTMLElement ? (pill.dataset.discountCode || '').trim() : '';
      })
      .filter(Boolean);
  }

  function mergeDiscountCodes(existingCodes, nextCode) {
    const merged = [];
    const seen = new Set();

    [...(existingCodes || []), nextCode].forEach(function appendCode(value) {
      const code = (value || '').toString().trim();
      const key = normalizeDiscountCode(code);
      if (!code || !key || seen.has(key)) return;
      seen.add(key);
      merged.push(code);
    });

    return merged;
  }

  function getDiscountApplication(data, requestedCode) {
    const requestedKey = normalizeDiscountCode(requestedCode);
    if (!requestedKey || !data || !Array.isArray(data.discount_codes)) return null;

    return data.discount_codes.find(function findCode(discount) {
      return normalizeDiscountCode(discount && discount.code) === requestedKey;
    }) || null;
  }

  function assertDiscountApplied(data, requestedCode) {
    const application = getDiscountApplication(data, requestedCode);
    if (application && application.applicable === true) return application;

    const error = new Error('Shopify did not apply the consultation discount code');
    error.name = 'ConsultationDiscountError';
    error.code = 'discount_not_applicable';
    error.discountCode = requestedCode;
    error.response = data;
    throw error;
  }

  function dispatchCartUpdate(data, resource) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: resource || null,
        sourceId: 'tara-consultation-results',
        data: data || {},
      },
    }));
  }

  function dispatchDiscountUpdate(resource) {
    const sections = resource && typeof resource === 'object' && resource.sections && typeof resource.sections === 'object'
      ? resource.sections
      : {};

    document.dispatchEvent(new CustomEvent('discount:update', {
      bubbles: true,
      detail: {
        resource: resource || null,
        sourceId: 'tara-consultation-results',
        data: {
          source: 'product-form-component',
          sections,
        },
      },
    }));
  }

  function afterUiFlush() {
    return new Promise(function afterFlush(resolve) {
      requestAnimationFrame(function onFrame() {
        requestAnimationFrame(resolve);
      });
    });
  }

  async function revealAppliedDiscounts() {
    await afterUiFlush();

    document.querySelectorAll('cart-discount-component').forEach(function reveal(component) {
      if (!(component instanceof HTMLElement)) return;
      if (!component.querySelector('.cart-discount__pill')) return;

      const disclosure = component.closest('details');
      if (disclosure instanceof HTMLDetailsElement) {
        disclosure.open = true;
      }
    });

    await afterUiFlush();
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.setAttribute('data-cart-confirmation', 'true');
      drawer.open();
      return true;
    }

    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger instanceof HTMLElement) {
      trigger.click();
      return true;
    }

    return false;
  }

  function resetButton(btn, originalText, className, nextText, delay) {
    btn.classList.remove('tara-qr__atc--adding');
    btn.classList.add(className);
    btn.textContent = nextText;
    btn.disabled = false;
    window.setTimeout(function restore() {
      btn.classList.remove(className);
      btn.removeAttribute('data-added');
      btn.textContent = originalText;
    }, delay);
  }

  async function applyDiscountCode(code, sectionIds) {
    const discountCode = (code || '').toString().trim();
    if (!discountCode) return null;
    const requestedCodes = mergeDiscountCodes(getCurrentDiscountCodes(), discountCode);

    const response = await fetch(resolveCartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        discount: requestedCodes.join(','),
        sections: sectionIds || [],
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error((data && data.description) || `Discount update failed (${response.status})`);
    }

    assertDiscountApplied(data, discountCode);
    dispatchDiscountUpdate(data);
    await revealAppliedDiscounts();
    return data;
  }

  async function redirectToCheckout(offerActivation) {
    const targetUrl = buildCheckoutRedirectUrl(offerActivation);
    return performCheckoutRedirect(targetUrl);
  }

  function createHelpers(options) {
    const getState = options && typeof options.getState === 'function'
      ? options.getState
      : function defaultState() { return null; };

    return {
      normalizeKey,
      esc,
      escAttr,
      formatMoney,
      toMinorUnits,
      replaceTokens,
      resolveCartAddUrl,
      resolveCartUpdateUrl,
      getCartSectionIds,
      getCurrentDiscountCodes,
      mergeDiscountCodes,
      getDiscountApplication,
      revealAppliedDiscounts,
      openCartDrawer,
      redirectToCheckout,
      buildCheckoutRedirectUrl,
      async addToCart(variantId, btn, quantity, offerActivation, flowOptions) {
        if (!variantId || !btn || btn.disabled || btn.classList.contains('tara-qr__atc--adding')) return;

        const state = getState();
        const t = state && state.config ? state.config.i18n || {} : {};
        const originalText = btn.textContent;
        const sectionIds = getCartSectionIds();
        const itemCount = Number(quantity) || 1;
        const behavior = Object.assign({
          openDrawerOnSuccess: true,
          redirectToCheckout: false,
        }, flowOptions || {});

        btn.classList.add('tara-qr__atc--adding');
        btn.setAttribute('data-added', 'true');
        btn.disabled = true;
        btn.textContent = t.adding || 'Adding...';

        try {
          const response = await fetch(resolveCartAddUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: itemCount,
                },
              ],
              sections: sectionIds.join(','),
            }),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            resetButton(btn, originalText, 'tara-qr__atc--error', data.description || t.error || 'Error', 3000);
            return {
              ok: false,
              stage: 'add',
              data,
            };
          }

          dispatchCartUpdate({
            source: 'product-form-component',
            itemCount,
            sections: data.sections,
          }, data);
          window.TaraMetaSignals?.routineAdded(state, [{ id: variantId, quantity: itemCount }]);

          if (offerActivation && offerActivation.code) {
            try {
              await applyDiscountCode(offerActivation.code, sectionIds);
            } catch (error) {
              console.error('[Tara Consultation] Discount apply failed:', error);
              resetButton(btn, originalText, 'tara-qr__atc--error', t.error || 'Error', 3000);
              return {
                ok: false,
                stage: 'discount',
                error,
              };
            }
          }

          if (behavior.redirectToCheckout) {
            btn.classList.remove('tara-qr__atc--adding');
            btn.classList.add('tara-qr__atc--added');
            btn.textContent = t.redirecting || 'Redirecting...';
            return {
              ok: true,
              redirected: true,
              url: await redirectToCheckout(offerActivation),
            };
          }

          resetButton(btn, originalText, 'tara-qr__atc--added', t.added || 'Added', 2000);

          if (behavior.openDrawerOnSuccess !== false) {
            openCartDrawer();
          }

          return {
            ok: true,
            redirected: false,
            data,
          };
        } catch (error) {
          console.error('[Tara Consultation] Add to cart failed:', error);
          btn.classList.remove('tara-qr__atc--adding');
          btn.removeAttribute('data-added');
          btn.disabled = false;
          btn.textContent = originalText;
          return {
            ok: false,
            stage: 'request',
            error,
          };
        }
      },
      async addItemsToCart(items, btn, offerActivation, flowOptions) {
        if (!Array.isArray(items) || !items.length || !btn || btn.disabled || btn.classList.contains('tara-qr__atc--adding')) return;

        const state = getState();
        const t = state && state.config ? state.config.i18n || {} : {};
        const originalText = btn.textContent;
        const sectionIds = getCartSectionIds();
        const itemCount = items.reduce(function sum(total, item) {
          return total + (Number(item.quantity) || 0);
        }, 0);
        const behavior = Object.assign({
          openDrawerOnSuccess: true,
          redirectToCheckout: false,
        }, flowOptions || {});
        const groupRoutine = behavior.groupRoutine !== false;
        const routineGroupId = behavior.routineGroupId || `tara-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        const routineTitle = String(behavior.routineTitle || t.yourRoutine || '').trim();

        btn.classList.add('tara-qr__atc--adding');
        btn.setAttribute('data-added', 'true');
        btn.disabled = true;
        btn.textContent = t.adding || 'Adding...';

        try {
          const response = await fetch(resolveCartAddUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: items.map(function mapItem(item, index) {
                const cartItem = {
                  id: item.id,
                  quantity: item.quantity,
                };
                const properties = Object.assign({}, item.properties || {});
                if (groupRoutine) {
                  Object.assign(properties, {
                    _tara_routine_id: routineGroupId,
                    _tara_routine_title: routineTitle,
                    _tara_routine_index: index + 1,
                    _tara_routine_size: items.length,
                  });
                }
                if (Object.keys(properties).length) cartItem.properties = properties;
                return cartItem;
              }),
              sections: sectionIds.join(','),
            }),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            resetButton(btn, originalText, 'tara-qr__atc--error', data.description || t.error || 'Error', 3000);
            return {
              ok: false,
              stage: 'add',
              data,
            };
          }

          dispatchCartUpdate({
            source: 'product-form-component',
            itemCount,
            sections: data.sections,
          }, data);
          window.TaraMetaSignals?.routineAdded(state, items);

          if (offerActivation && offerActivation.code) {
            try {
              await applyDiscountCode(offerActivation.code, sectionIds);
            } catch (error) {
              console.error('[Tara Consultation] Discount apply failed:', error);
              resetButton(btn, originalText, 'tara-qr__atc--error', t.error || 'Error', 3000);
              return {
                ok: false,
                stage: 'discount',
                error,
              };
            }
          }

          if (behavior.redirectToCheckout) {
            btn.classList.remove('tara-qr__atc--adding');
            btn.classList.add('tara-qr__atc--added');
            btn.textContent = t.redirecting || 'Redirecting...';
            return {
              ok: true,
              redirected: true,
              url: await redirectToCheckout(offerActivation),
            };
          }

          resetButton(btn, originalText, 'tara-qr__atc--added', t.added || 'Added', 2000);

          if (behavior.openDrawerOnSuccess !== false) {
            openCartDrawer();
          }

          return {
            ok: true,
            redirected: false,
            data,
          };
        } catch (error) {
          console.error('[Tara Consultation] Add routine to cart failed:', error);
          btn.classList.remove('tara-qr__atc--adding');
          btn.removeAttribute('data-added');
          btn.disabled = false;
          btn.textContent = originalText;
          return {
            ok: false,
            stage: 'request',
            error,
          };
        }
      },
    };
  }

  window.TaraConsultationResultsCart = Object.freeze({
    normalizeKey,
    esc,
    escAttr,
    formatMoney,
    toMinorUnits,
    replaceTokens,
    getCurrentDiscountCodes,
    mergeDiscountCodes,
    getDiscountApplication,
    assertDiscountApplied,
    buildCheckoutRedirectUrl,
    redirectToCheckout,
    createHelpers,
  });
})();
